import { useCallback, useContext } from "react";
import axios from "axios";

import AuthContext from "common/store/AuthContext";
import { genApiDatePath, generateErrorMsg, getServerUrl } from "utils/commonTools";

import { WeatherDataMeta } from "../types/WeatherDataMeta";
import { BasicResponse, GetBasicResponse } from "common/types/responses/BasicResponse";
import { WeatherData } from "../types/WeatherData";
import { REPORT_TYPE } from "common/types/consts/Defines";
import { toJapaneseDate } from "utils/DateTools";
import { DateAndValue } from "common/types/DateAndValue";
import { WeatherAPIData } from "../types/WeatherApiData";

export const useWeather = () => {
  const authCtxt = useContext(AuthContext);
  const { token, selectedPark } = authCtxt;

  //--------------------------------------
  // 定義済み天気一覧を取得する
  //--------------------------------------
  const getWeatherPatterns = useCallback(
    async (): Promise<{ succeeded: boolean; msg: string; data?: WeatherDataMeta }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };

      try {
        const { data } = await axios.get(getServerUrl() + `weather/meta`, config);
        const metaData = data as GetBasicResponse<WeatherDataMeta>;
        if (!metaData) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }
        return {
          succeeded: true,
          msg: "",
          data: metaData.data,
        };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPark]
  );

  //--------------------------------------
  // 天気データをサーバーから取得する
  //--------------------------------------
  const getWeatherData = useCallback(
    async (
      parkId: number,
      date: Date,
      reportType: REPORT_TYPE
    ): Promise<{
      succeeded: boolean;
      msg: string;
      data?: WeatherData;
    }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };
      try {
        // 日報データの取得
        const url = getServerUrl() + `weather/${parkId}/${genApiDatePath(date)}/${reportType}`;

        const { data } = await axios.get(url, config);
        const reportData = data as GetBasicResponse<WeatherData>;
        if (!reportData) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }

        return {
          succeeded: true,
          msg: "",
          data: reportData.data,
        };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [token]
  );

  //--------------------------------------
  // 天気を保存する
  //--------------------------------------
  const saveWeatherData = useCallback(
    async (
      parkId: number,
      date: Date,
      weatherData: WeatherData | undefined
    ): Promise<{ succeeded: boolean; msg: string }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };
      try {
        const url = getServerUrl() + `weather/${parkId}/${genApiDatePath(date)}`;
        const { data } = await axios.post(url, weatherData, config);

        const responseData = data as BasicResponse;
        if (!responseData) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }

        authCtxt.updateToken(responseData.token, new Date(responseData.expiresAt));

        return { succeeded: true, msg: "" };
      } catch (error) {
        const errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [authCtxt, token]
  );

  //--------------------------------------
  // 期間の天気データをサーバーから取得する
  //--------------------------------------
  const getWeatherDataByPeriod = useCallback(
    async (
      parkId: number,
      from: Date,
      to: Date
    ): Promise<{
      succeeded: boolean;
      msg: string;
      data?: DateAndValue<WeatherAPIData>[];
    }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
        params: {
          from: toJapaneseDate(from),
          to: toJapaneseDate(to),
        },
      };
      try {
        const url = getServerUrl() + `weather/${parkId}/range`;
        const { data } = await axios.get<DateAndValue<WeatherAPIData>[]>(url, config);

        if (!data) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }

        return {
          succeeded: true,
          msg: "",
          data,
        };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [token]
  );

  return { getWeatherPatterns, getWeatherData, saveWeatherData, getWeatherDataByPeriod };
};
